import { apiUrl } from './config';

export interface PublicTemplateItem {
  id: string;
  name: string;
  slug: string;
  category: string;
  description?: string;
  isPremium: boolean;
  updatedAt?: string;
  thumbnail?: string | null;
  thumbnailLarge?: string | null;
  thumbnailMedium?: string | null;
  image?: string | null;
  html?: string | null;
  json?: any;
}

function versionedThumbnail(url: string | null | undefined): string | null {
  if (!url) return null;
  const clean = url.replace(/\?v=[0-9]+/, '');
  return `${clean}?v=10`;
}

export function cleanTemplateDescription(rawDescription: unknown): string | undefined {
  let description: unknown = rawDescription;
  if (typeof description === 'object' && description !== null) {
    const localized = description as Record<string, unknown>;
    description = localized.en ?? localized.description ?? localized.text;
  }
  if (typeof description !== 'string') return undefined;

  const normalized = description.replace(/\s+/g, ' ').trim();
  return normalized || undefined;
}

function titleFromSlug(slug?: string): string {
  if (!slug) return 'Email Template';
  const clean = slug
    .replace(/^(email-template-|template-|market-|cmlv_mkt_)/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\d{4}\b/g, '')
    .trim();
  return clean
    .split(' ')
    .filter(Boolean)
    .map((w) => {
      const low = w.toLowerCase();
      if (['hr', 'saas', 'b2b', 'vip', 'ai', 'devops', 'api', 'ui'].includes(low)) return low.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ') || 'Email Template';
}

function hasUsableTemplateName(rawName: unknown, slug?: string): boolean {
  const name = cleanTemplateName(rawName, slug);
  return name !== 'Untitled Template' && name !== 'Email Template' && name !== 'Loading...';
}

export function cleanTemplateName(rawName: any, slug?: string): string {
  if (!rawName && slug) {
    return titleFromSlug(slug);
  }
  let name = '';
  if (typeof rawName === 'string') {
    name = rawName;
  } else if (typeof rawName === 'object' && rawName !== null) {
    name = rawName.en || rawName.name || rawName.title || '';
  } else {
    name = String(rawName || '');
  }

  // Strip '[object Object] - ' if present
  let cleaned = name.replace(/^\[object Object\]\s*-\s*/i, '').trim();

  // If name has a company/brand prefix like "Brand - Template Name", keep the meaningful template name
  if (cleaned.includes(' - ')) {
    const parts = cleaned.split(' - ');
    if (parts.length > 1 && parts[1]?.trim()) {
      cleaned = parts.slice(1).join(' - ').trim();
    }
  }

  if ((!cleaned || cleaned === 'Untitled Template' || cleaned === 'Email Template') && slug) {
    return titleFromSlug(slug);
  }

  return cleaned || 'Email Template';
}

const CATEGORY_NAMES: Record<string, string> = {
  all: 'All',
  commerce: 'Commerce',
  ecommerce: 'Commerce',
  service: 'Customer Service',
  'customer service': 'Customer Service',
  billing: 'Billing & Payments',
  'billing & payments': 'Billing & Payments',
  account: 'Account & Security',
  'account & security': 'Account & Security',
  nonprofit: 'Nonprofit',
  'nonprofit & impact': 'Nonprofit',
  property: 'Real Estate',
  'real estate': 'Real Estate',
  hr: 'HR & Teams',
  'hr & teams': 'HR & Teams',
  healthcare: 'Healthcare',
  education: 'Education',
  saas: 'SaaS',
  technology: 'Technology',
  travel: 'Travel',
  'travel & hospitality': 'Travel',
  fashion: 'Fashion',
  culinary: 'Culinary',
  'food & dining': 'Culinary',
  food: 'Culinary',
  luxury: 'Luxury',
  fitness: 'Fitness',
  'wellness & fitness': 'Fitness',
  events: 'Events',
  beauty: 'Beauty',
  publishing: 'Publishing',
  interiors: 'Interiors',
  botanical: 'Botanical',
  arts: 'Arts & Culture',
  'arts & culture': 'Arts & Culture',
  craft: 'Craft',
  audio_auto: 'Audio & Auto',
  'audio & auto': 'Audio & Auto',
  couture: 'Couture',
  coastal_cuisine: 'Coastal Dining',
  'coastal dining': 'Coastal Dining',
  renewable_architecture: 'Green Architecture',
  'green architecture': 'Green Architecture',
  pet_boutique: 'Pet Boutique',
  'pet boutique': 'Pet Boutique',
  kids_learning: 'Early Learning',
  'early learning': 'Early Learning',
  thermal_spa: 'Thermal Spa',
  'thermal spa': 'Thermal Spa',
  jewelry_atelier: 'Jewelry Atelier',
  'jewelry atelier': 'Jewelry Atelier',
  alpine_outdoor: 'Alpine & Outdoor',
  'alpine & outdoor': 'Alpine & Outdoor',
  patisserie: 'Patisserie',
  electric_auto: 'Electric Auto',
  'electric auto': 'Electric Auto',
  vineyard: 'Vineyard',
  performing_arts: 'Performing Arts',
  'performing arts': 'Performing Arts',
  beauty_science: 'Beauty Science',
  'beauty science': 'Beauty Science',
  sailing: 'Sailing',
  ceramic_tea: 'Ceramic & Tea',
  'ceramic & tea': 'Ceramic & Tea',
  newsletter: 'Newsletter',
  'developer tools': 'Developer Tools',
  b2b: 'B2B',
  fintech: 'Fintech',
  'media & entertainment': 'Media & Entertainment',
  transactional: 'Transactional',
};

export function formatCategoryName(cat?: string | null): string {
  if (!cat) return 'General';
  const key = cat.toLowerCase().trim();
  if (CATEGORY_NAMES[key]) return CATEGORY_NAMES[key];
  const formatted = cat.replace(/[_-]+/g, ' ').trim();
  return formatted
    .split(' ')
    .filter(Boolean)
    .map((w) => {
      const low = w.toLowerCase();
      if (['hr', 'saas', 'b2b', 'vip', 'ai', 'ui', 'api'].includes(low)) return low.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

export function slugifyText(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/['"’]/g, '')
    .replace(/[^\w\s-]/g, ' ')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const API_ENDPOINT = apiUrl('/api/templates?limit=2500&sort=updated');

// In-memory cache per worker / process
let inMemoryTemplates: PublicTemplateItem[] | null = null;
const templateBySlugOrId = new Map<string, PublicTemplateItem>();

function getDiskCache(): PublicTemplateItem[] | null {
  if (typeof window !== 'undefined') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const fs = require('fs');
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const path = require('path');
    const cacheFile = path.join(process.cwd(), '.next', 'postbrix-templates-cache.json');
    if (fs.existsSync(cacheFile)) {
      const stats = fs.statSync(cacheFile);
      // Evict disk cache if older than 24 hours
      if (Date.now() - stats.mtimeMs > 24 * 60 * 60 * 1000) {
        return null;
      }
      const raw = fs.readFileSync(cacheFile, 'utf8');
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return null;
}

function setDiskCache(templates: PublicTemplateItem[]): void {
  if (typeof window !== 'undefined') return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const fs = require('fs');
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const path = require('path');
    const cacheDir = path.join(process.cwd(), '.next');
    if (!fs.existsSync(cacheDir)) {
      fs.mkdirSync(cacheDir, { recursive: true });
    }
    const cacheFile = path.join(cacheDir, 'postbrix-templates-cache.json');
    fs.writeFileSync(cacheFile, JSON.stringify(templates), 'utf8');
  } catch {
    // ignore
  }
}

function populateMemoryCache(templates: PublicTemplateItem[]) {
  inMemoryTemplates = templates;
  for (const t of templates) {
    if (t.slug) {
      templateBySlugOrId.set(t.slug, t);
      templateBySlugOrId.set(t.slug.toLowerCase(), t);
    }
    if (t.id) {
      templateBySlugOrId.set(t.id, t);
      templateBySlugOrId.set(t.id.toLowerCase(), t);
    }
    if (t.name) {
      const nameSlug = slugifyText(t.name);
      if (nameSlug && !templateBySlugOrId.has(nameSlug)) {
        templateBySlugOrId.set(nameSlug, t);
      }
    }
  }
}

async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retries = 2,
  timeoutMs = 35000
): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (res.ok) return res;
      if (res.status >= 500 && attempt < retries) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      return res;
    } catch (err) {
      lastError = err;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

function parseTemplatePage(payload: unknown): {
  items: unknown[] | null;
  hasMore?: boolean;
  nextPage?: number;
  totalPages?: number;
  total?: number;
} {
  if (Array.isArray(payload)) return { items: payload };
  if (!payload || typeof payload !== 'object') return { items: null, hasMore: false };

  const envelope = payload as Record<string, unknown>;
  const data = envelope.data && typeof envelope.data === 'object'
    ? envelope.data as Record<string, unknown>
    : null;
  const items = Array.isArray(envelope.templates)
    ? envelope.templates
    : Array.isArray(data?.templates)
      ? data.templates
      : Array.isArray(data)
        ? data
        : null;
  const pagination = (envelope.pagination || envelope.meta || data?.pagination || data?.meta) as Record<string, unknown> | undefined;
  const totalPagesValue = pagination?.totalPages ?? pagination?.pageCount;
  const totalValue = pagination?.total ?? pagination?.totalItems;
  const nextPageValue = pagination?.nextPage;

  return {
    items,
    hasMore: typeof pagination?.hasMore === 'boolean'
      ? pagination.hasMore
      : typeof pagination?.hasNextPage === 'boolean'
        ? pagination.hasNextPage
        : undefined,
    nextPage: typeof nextPageValue === 'number' ? nextPageValue : undefined,
    totalPages: typeof totalPagesValue === 'number' ? totalPagesValue : undefined,
    total: typeof totalValue === 'number' ? totalValue : undefined,
  };
}

async function fetchTemplateCatalog(): Promise<unknown[]> {
  const allItems: unknown[] = [];
  const seenIds = new Set<string>();
  let page = 1;
  let hasMore = true;
  let totalPages: number | undefined;
  let total: number | undefined;

  while (hasMore) {
    const endpoint = new URL(API_ENDPOINT);
    endpoint.searchParams.set('page', String(page));
    const response = await fetchWithRetry(endpoint.toString(), {}, 2, 45000);

    if (!response.ok) {
      throw new Error(`Template API returned ${response.status} on page ${page}; refusing to build an incomplete catalog.`);
    }

    const payload = await response.json();
    const result = parseTemplatePage(payload);
    if (!result.items) throw new Error('Template API response did not contain a template list.');
    if (result.totalPages !== undefined) totalPages = result.totalPages;
    if (result.total !== undefined) total = result.total;

    let added = 0;
    for (const item of result.items) {
      const record = item && typeof item === 'object' ? item as Record<string, unknown> : null;
      const identifier = record && (record.id ?? record.slug);
      const key = typeof identifier === 'string' ? identifier : JSON.stringify(item);
      if (!seenIds.has(key)) {
        seenIds.add(key);
        allItems.push(item);
        added += 1;
      }
    }

    if (result.hasMore !== undefined) {
      hasMore = result.hasMore;
    } else if (totalPages !== undefined) {
      hasMore = page < totalPages;
    } else if (total !== undefined) {
      hasMore = allItems.length < total;
    } else if (Array.isArray(payload)) {
      const currentLimit = Number(endpoint.searchParams.get('limit')) || 2500;
      hasMore = payload.length >= currentLimit && added > 0;
    } else {
      hasMore = false;
    }

    if (hasMore && added === 0) {
      break;
    }
    page = result.nextPage ?? page + 1;
  }

  return allItems;
}

export async function fetchAllPublicTemplates(): Promise<PublicTemplateItem[]> {
  // 1. Check in-memory cache
  if (inMemoryTemplates && inMemoryTemplates.length > 0) {
    return inMemoryTemplates;
  }

  // 2. Check disk cache across build workers
  const diskCache = getDiskCache();
  if (diskCache && diskCache.length > 0) {
    populateMemoryCache(diskCache);
    return diskCache;
  }

  const rawList = await fetchTemplateCatalog();

  const validList = rawList.filter((item: any) =>
    item && item.isPublic !== false && hasUsableTemplateName(item.name || item.title, item.slug || item.id)
  );

  if (validList.length === 0) {
    throw new Error('Template API returned no usable templates; refusing to publish an empty catalog.');
  }

  const mappedByRoute = new Map<string, PublicTemplateItem>();
  validList.forEach((item: any) => {
    const id = String(item.id || item.slug || '');
    const slug = String(item.slug || item.id || '');
    if (!id || !slug) return;
    mappedByRoute.set(slug, {
      id,
      name: cleanTemplateName(item.name || item.title, slug),
      slug,
      category: item.category || 'General',
      description: cleanTemplateDescription(item.description),
      isPremium: Boolean(item.isPremium),
      updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : undefined,
      thumbnail: versionedThumbnail(item.thumbnail),
      thumbnailLarge: versionedThumbnail(item.thumbnailLarge),
      thumbnailMedium: versionedThumbnail(item.thumbnailMedium),
      image: versionedThumbnail(item.thumbnailLarge || item.thumbnailMedium || item.thumbnail),
      html: item.html || null,
      json: null,
    });
  });
  const mapped = Array.from(mappedByRoute.values());

  // Pre-fetch rendered HTML only for templates that lack an image preview
  const missingHtmlTemplates = mapped.filter((t) => !t.html && !t.image && t.id);
  if (missingHtmlTemplates.length > 0) {
    for (let offset = 0; offset < missingHtmlTemplates.length; offset += 8) {
      const batch = missingHtmlTemplates.slice(offset, offset + 8);
      await Promise.all(batch.map(async (template) => {
        try {
          const renderResponse = await fetchWithRetry(apiUrl(`/api/render-email/${encodeURIComponent(template.id)}?preview=true`), {}, 2, 10000);
          if (renderResponse.ok) {
            const htmlText = await renderResponse.text();
            if (htmlText && htmlText.length > 50 && !htmlText.includes('ASTValidationError')) {
              template.html = htmlText;
            }
          }
        } catch {
          // Keep template with null html; preview frame handles thumbnail/mockup fallback
        }
      }));
    }
  }

  populateMemoryCache(mapped);
  setDiskCache(mapped);
  return mapped;
}

export async function fetchSingleTemplate(slug: string): Promise<PublicTemplateItem | null> {
  const normalizedSlug = slug.trim().toLowerCase();

  // 1. Check in-memory map
  if (templateBySlugOrId.has(slug)) {
    const item = templateBySlugOrId.get(slug)!;
    if (item.html) return item;
  }
  if (templateBySlugOrId.has(normalizedSlug)) {
    const item = templateBySlugOrId.get(normalizedSlug)!;
    if (item.html) return item;
  }

  // 2. Populate memory & disk cache
  if (!inMemoryTemplates || inMemoryTemplates.length === 0) {
    await fetchAllPublicTemplates();
    if (templateBySlugOrId.has(slug)) {
      const item = templateBySlugOrId.get(slug)!;
      if (item.html) return item;
    }
    if (templateBySlugOrId.has(normalizedSlug)) {
      const item = templateBySlugOrId.get(normalizedSlug)!;
      if (item.html) return item;
    }
  }

  // 3. Fallback fuzzy / variant search in inMemoryTemplates
  if (inMemoryTemplates && inMemoryTemplates.length > 0) {
    const match = inMemoryTemplates.find((t) => {
      if (t.slug && t.slug.toLowerCase() === normalizedSlug) return true;
      if (t.id && t.id.toLowerCase() === normalizedSlug) return true;
      if (t.name && slugifyText(t.name) === normalizedSlug) return true;
      if (t.slug && (normalizedSlug.startsWith(t.slug) || t.slug.startsWith(normalizedSlug))) return true;
      return false;
    });

    if (match) {
      templateBySlugOrId.set(slug, match);
      if (match.html) return match;
    }
  }

  // 4. Query single template directly from API
  try {
    const res = await fetchWithRetry(apiUrl(`/api/templates/${encodeURIComponent(slug)}`), {}, 1, 10000);
    if (res.ok) {
      const item = await res.json();
      if (item && (item.id || item.slug)) {
        const resolved: PublicTemplateItem = {
          id: String(item.id || item.slug),
          name: cleanTemplateName(item.name || item.title),
          slug: item.slug || item.id,
          category: item.category || 'General',
          description: cleanTemplateDescription(item.description),
          isPremium: Boolean(item.isPremium),
          updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : undefined,
          thumbnail: versionedThumbnail(item.thumbnail),
          thumbnailLarge: versionedThumbnail(item.thumbnailLarge),
          thumbnailMedium: versionedThumbnail(item.thumbnailMedium),
          image: versionedThumbnail(item.thumbnailLarge || item.thumbnailMedium || item.thumbnail),
          html: item.html || null,
          json: null,
        };
        templateBySlugOrId.set(slug, resolved);
        if (resolved.slug) templateBySlugOrId.set(resolved.slug, resolved);
        if (resolved.id) templateBySlugOrId.set(resolved.id, resolved);
        return resolved;
      }
    }
  } catch {
    // Return null if not found
  }

  return null;
}

export async function getAllTemplateSlugs(): Promise<{ slug: string }[]> {
  try {
    const templates = await fetchAllPublicTemplates();
    const slugs = new Set<string>();

    templates.forEach((t) => {
      const canonical = t.slug || t.id;
      if (canonical) slugs.add(canonical);
    });

    return Array.from(slugs).map((slug) => ({ slug }));
  } catch {
    return [];
  }
}
